import PropTypes from 'prop-types';
import React from 'react';

import { debounce } from '@wetransfer/concorde-debounce';

/**
 * HoC that can be used to lazy load components
 */
export default class LazyLoad extends React.Component {
  static propTypes = {
    children: PropTypes.node,
    backgroundColor: PropTypes.string,
    enabled: PropTypes.bool,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onVisible: PropTypes.func,
    scrollEl: PropTypes.object,
    threshold: PropTypes.number,
    throttle: PropTypes.number,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  };

  static defaultProps = {
    backgroundColor: 'transparent',
    enabled: true,
    height: 'inherit',
    scrollEl: window,
    threshold: 10,
    throttle: 100,
    width: 'inherit',
  };

  state = {
    visible: !this.props.enabled,
  };

  constructor(props) {
    super(props);
    this.checkVisible = debounce(
      this.checkVisible.bind(this),
      this.props.throttle
    );
  }

  /**
   * Checks whether an element is in the vertical viewport. Can be used with a minimum threshold
   *
   * @param  {HTMLElement} el   The element to check
   * @param  {number} threshold The threshold in pixels
   * @return {Boolean}          Whether the element is in the viewport
   */
  inViewport(el, threshold = 0) {
    if (!el) {
      return false;
    }

    const winHeight = window.innerHeight;
    const { top, left, right, bottom } = el.getBoundingClientRect();

    const validRect = {
      top: top - threshold,
      left: left - threshold,
      right: right + threshold,
      bottom: bottom + threshold,
    };

    return validRect.bottom > 0 && validRect.top < winHeight;
  }

  /**
   * Sets the component state to visible and removes eventlisteners if the element is in viewport
   */
  checkVisible() {
    if (this.inViewport(this.node, this.props.threshold)) {
      this.setState({ visible: true });
      this.onLoaded();
      if (this.props.onVisible) {
        this.props.onVisible();
      }
    }
  }

  /**
   * Adds eventlistener to component and checks invokes visibility if visibility state if false.
   */
  componentDidMount() {
    if (!this.state.visible) {
      this.checkVisible();
    }

    if (this.props.enabled) {
      this.props.scrollEl &&
        this.props.scrollEl.addEventListener('scroll', this.checkVisible);
      window.addEventListener('resize', this.checkVisible);
    }
  }

  /**
   * Calls onLoaded method
   */
  componentWillUnmount() {
    this.onLoaded();
  }

  /**
   * Checks if something has changed that could affect visibility.
   */
  componentDidUpdate(prevProps) {
    if (!this.state.visible) {
      this.checkVisible();
    }

    if (prevProps && prevProps.scrollEl !== this.props.scrollEl) {
      prevProps.scrollEl &&
        prevProps.scrollEl.removeEventListener('scroll', this.checkVisible);
      this.props.scrollEl &&
        this.props.scrollEl.addEventListener('scroll', this.checkVisible);
    }
  }

  /**
   * Removes eventlisteners
   */
  onLoaded() {
    if (this.props.enabled) {
      this.props.scrollEl &&
        this.props.scrollEl.removeEventListener('scroll', this.checkVisible);
      window.removeEventListener('resize', this.checkVisible);
    }
  }

  /**
   * Renders children if component is in viewport.
   * @return {ReactElement} markup
   */
  render() {
    const style = {
      height: this.props.height,
      backgroundColor: this.props.backgroundColor,
    };

    return (
      <div ref={(node) => (this.node = node)} style={style}>
        {this.state.visible ? this.props.children : null}
      </div>
    );
  }
}
